import { Link } from "react-router-dom";
import { Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { useCart } from "../../context/CartContext";
import { useStore } from "../../context/StoreContext";
import { money } from "../../lib/api";

export const CartLine = ({ i }) => {
  const { updateQty, remove } = useCart();
  return (
    <div className="flex gap-4 py-4" data-testid={`cart-line-${i.slug}`}>
      <img src={i.image} alt={i.name} className="w-20 h-24 rounded-2xl object-cover bg-sand" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-navy leading-snug">{i.name}</p>
        <p className="text-xs text-navy/60 mt-0.5">{i.color} · {i.size}</p>
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center rounded-full border border-border">
            <button className="w-9 h-9 grid place-items-center" onClick={() => updateQty(i.key, i.qty - 1)} aria-label="Decrease" data-testid={`cart-qty-dec-${i.slug}`}><Minus className="w-4 h-4" /></button>
            <span className="w-6 text-center font-semibold" data-testid={`cart-qty-${i.slug}`}>{i.qty}</span>
            <button className="w-9 h-9 grid place-items-center" onClick={() => updateQty(i.key, i.qty + 1)} aria-label="Increase" data-testid={`cart-qty-inc-${i.slug}`}><Plus className="w-4 h-4" /></button>
          </div>
          <span className="font-bold text-navy">{money(i.price * i.qty)}</span>
        </div>
      </div>
      <button onClick={() => remove(i.key)} className="self-start p-2 text-navy/40 hover:text-tpink transition-colors" aria-label="Remove item" data-testid={`cart-remove-${i.slug}`}>
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

export const shippingFor = (subtotal, settings) =>
  subtotal === 0 ? 0 : subtotal >= (settings?.free_shipping_threshold ?? 75) ? 0 : settings?.flat_shipping ?? 5.99;

export const CartDrawer = () => {
  const { items, subtotal, open, setOpen } = useCart();
  const { settings } = useStore();
  const threshold = settings?.free_shipping_threshold ?? 75;
  const left = Math.max(threshold - subtotal, 0);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0" data-testid="cart-drawer">
        <div className="p-6 pb-4 border-b border-border">
          <SheetTitle className="font-brush text-4xl text-navy">Your Cart</SheetTitle>
          {items.length > 0 && (
            <p className="text-sm text-navy/70 mt-1" data-testid="cart-free-shipping-msg">
              {left > 0 ? <>You're <b>{money(left)}</b> away from free U.S. shipping</> : "You've unlocked free U.S. shipping!"}
            </p>
          )}
        </div>
        <div className="flex-1 overflow-y-auto px-6 divide-y divide-border">
          {items.length === 0 ? (
            <div className="py-20 text-center" data-testid="cart-empty">
              <ShoppingBag className="w-12 h-12 mx-auto text-turq" />
              <p className="font-brush text-3xl text-navy mt-4">Your bag is ready for takeoff</p>
              <Link to="/destinations" onClick={() => setOpen(false)} className="inline-block mt-6 rounded-full bg-sun px-6 py-3 font-bold text-navy">Find your destination</Link>
            </div>
          ) : (
            items.map((i) => <CartLine key={i.key} i={i} />)
          )}
        </div>
        {items.length > 0 && (
          <div className="p-6 border-t border-border space-y-3 bg-sand/60">
            <div className="flex justify-between font-bold text-navy text-lg">
              <span>Subtotal</span>
              <span data-testid="cart-drawer-subtotal">{money(subtotal)}</span>
            </div>
            <p className="text-xs text-navy/60">Shipping, taxes & discounts calculated at checkout.</p>
            <Link to="/cart" onClick={() => setOpen(false)} className="block text-center h-14 leading-[3.5rem] rounded-full bg-navy text-white font-bold" data-testid="cart-drawer-view-cart">
              View Cart & Checkout
            </Link>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
